<!-- src/shared/components/layout/TransactionalFooter.vue -->

<script setup lang="ts">
  import JurisdictionToggle from '@/shared/components/ui/JurisdictionToggle.vue';
  import LanguageToggle from '@/shared/components/ui/LanguageToggle.vue';
  import ThemeToggle from '@/shared/components/ui/ThemeToggle.vue';
  import { useCookieConsent } from '@/shared/composables/useCookieConsent';
  import { useFooterConfig } from '@/shared/composables/useFooterConfig';
  import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
  import { useProductIdentity } from '@/shared/stores/identityStore';
  import type { LayoutProps } from '@/types/ui/layouts';
  import { storeToRefs } from 'pinia';
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';

  // SeuID footer (SaaS layout, honest links only — every entry points at a
  // real route/action; no Blog/Pricing/Careers placeholders). Two tiers:
  //  · Tier 1: brand block (mark + wordmark + tagline + AES-256/LGPD trust
  //    line) and real link groups (Produto / Legal / Suporte).
  //  · Tier 2: copyright + rights-reserved (+ dev version) and the utility
  //    cluster (theme, language, jurisdiction).
  // Custom-domain/tenant deployments suppress the platform brand block and the
  // Produto/Suporte groups (showPlatformIdentity) so SeuID branding never leaks
  // onto a tenant surface; the legal group stays because those routes exist for
  // every deployment.
  withDefaults(defineProps<LayoutProps>(), {
    displayVersion: true,
    displayToggles: true,
    displayPoweredBy: true,
  });

  const { t } = useI18n();
  const bootstrapStore = useBootstrapStore();
  const {
    regions_enabled,
    regions,
    i18n_enabled,
    ot_version_long,
    brand_support_email,
  } = storeToRefs(bootstrapStore);

  const identityStore = useProductIdentity();
  const { isCustom, showPlatformIdentity, productName } = storeToRefs(identityStore);
  const { showVersionConfig } = useFooterConfig();
  const { openBanner, analyticsConfigured } = useCookieConsent();

  // Hide regions toggle on custom domains (they're tied to a specific deployment)
  const showRegionsToggle = computed(
    () => regions_enabled.value && !!regions?.value && !isCustom.value
  );

  // Two-tone wordmark only for our own product name — tenants/custom domains
  // keep a plain rendering, mirroring the masthead's isSeuidWordmark rule.
  const isSeuidWordmark = computed(
    () => productName.value?.trim().toLowerCase() === 'seuid'
  );

  // Copyright entity: our own domain for the platform, the tenant's product
  // name on custom domains (never hard-code SeuID onto a tenant footer).
  const copyrightName = computed(() =>
    isSeuidWordmark.value ? 'SeuID.com.br' : productName.value || ''
  );

  // The "Cookies" affordance only makes sense when analytics is configured
  // (otherwise there is no consent to manage). analyticsConfigured is already a
  // ComputedRef; openBanner() reopens the consent bar so a prior choice can be
  // revoked.
  const showCookies = analyticsConfigured;
  const supportEmail = computed(() => brand_support_email?.value || '');

  const currentYear = new Date().getFullYear();

  const groupHeadClass =
    'font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-day-ink-faint dark:text-night-ink-faint';
  const linkClass =
    'font-mono text-[11px] text-day-ink-muted transition-colors hover:text-brand-600 dark:text-night-ink-muted dark:hover:text-brand-500';
</script>

<template>
  <footer
    class="w-full min-w-[320px] border-t border-day-line bg-day
      transition-colors duration-300 dark:border-white/[0.07] dark:bg-night"
    :aria-label="t('web.layout.site_footer')">
    <div class="mx-auto max-w-7xl px-5 py-10 sm:px-8">
      <!-- Tier 1: brand + link groups -->
      <div class="flex flex-col gap-10 md:flex-row md:items-start md:justify-between">
        <!-- Brand block -->
        <div
          v-if="showPlatformIdentity"
          class="max-w-xs">
          <div class="flex items-center gap-2">
            <img
              :src="'/favicon.svg'"
              alt=""
              class="size-5"
              aria-hidden="true" />
            <span
              v-if="isSeuidWordmark"
              class="font-mono text-sm font-bold text-day-ink dark:text-night-ink">seu<span class="text-brand-600 dark:text-brand-500">id</span></span>
            <span
              v-else
              class="font-mono text-sm font-bold text-day-ink dark:text-night-ink">
              {{ productName }}
            </span>
          </div>
          <p class="mt-3 text-xs leading-relaxed text-day-ink-muted dark:text-night-ink-muted">
            {{ t('web.footer.tagline') }}
          </p>
          <p class="mt-3 font-mono text-[10px] tracking-[0.08em] text-day-ink-faint uppercase dark:text-night-ink-faint">
            {{ t('web.footer.trust') }}
          </p>
        </div>

        <!-- Link groups -->
        <nav
          class="grid grid-cols-2 gap-8 sm:gap-12"
          :class="showPlatformIdentity ? 'sm:grid-cols-3' : 'sm:grid-cols-2'"
          :aria-label="t('web.layout.site_footer')">
          <!-- Produto (platform only) -->
          <div v-if="showPlatformIdentity">
            <h3 :class="groupHeadClass">
              {{ t('web.footer.product') }}
            </h3>
            <ul class="mt-3 space-y-2.5">
              <li>
                <router-link to="/" :class="linkClass">
                  {{ t('web.COMMON.button_create_secret') }}
                </router-link>
              </li>
              <li>
                <router-link to="/signin" :class="linkClass">
                  {{ t('web.login.button_sign_in') }}
                </router-link>
              </li>
              <li>
                <router-link to="/signup" :class="linkClass">
                  {{ t('web.homepage.sign_up_free') }}
                </router-link>
              </li>
              <li>
                <router-link to="/docs" :class="linkClass">
                  {{ t('web.footer.docs') }}
                </router-link>
              </li>
              <li>
                <router-link to="/status" :class="linkClass">
                  {{ t('web.COMMON.status') }}
                </router-link>
              </li>
            </ul>
          </div>

          <!-- Legal (always) -->
          <div>
            <h3 :class="groupHeadClass">
              {{ t('web.footer.legals') }}
            </h3>
            <ul class="mt-3 space-y-2.5">
              <li>
                <router-link to="/privacidade" :class="linkClass">
                  {{ t('web.footer.privacy') }}
                </router-link>
              </li>
              <li>
                <router-link to="/termos" :class="linkClass">
                  {{ t('web.footer.terms') }}
                </router-link>
              </li>
              <li v-if="showCookies">
                <button
                  type="button"
                  :class="linkClass"
                  @click="openBanner">
                  {{ t('web.footer.cookies') }}
                </button>
              </li>
            </ul>
          </div>

          <!-- Suporte (platform only, when a support address is configured) -->
          <div v-if="showPlatformIdentity && supportEmail">
            <h3 :class="groupHeadClass">
              {{ t('web.footer.support') }}
            </h3>
            <ul class="mt-3 space-y-2.5">
              <li>
                <a :href="`mailto:${supportEmail}`" :class="linkClass">
                  {{ t('web.footer.contact') }}
                </a>
              </li>
            </ul>
          </div>
        </nav>
      </div>

      <!-- Tier 2: copyright + utilities -->
      <div
        class="mt-10 flex flex-col-reverse items-center justify-between gap-4 border-t
          border-day-line pt-6 md:flex-row dark:border-white/[0.07]">
        <div class="flex flex-wrap items-center justify-center gap-2 md:justify-start">
          <span class="font-mono text-[10px] text-day-ink-faint dark:text-night-ink-faint">
            {{ t('web.footer.copyright', { year: currentYear, name: copyrightName }) }}
          </span>
          <template v-if="displayVersion && showVersionConfig">
            <span
              class="hidden text-day-line-2 sm:inline dark:text-white/[0.13]"
              aria-hidden="true">·</span>
            <span class="font-mono text-[10px] text-day-ink-faint dark:text-night-ink-faint">
              v{{ ot_version_long }}
            </span>
          </template>
        </div>

        <!-- Utility cluster: theme + language (+ jurisdiction) -->
        <div
          v-if="displayToggles"
          class="flex flex-wrap items-center justify-center gap-2 md:justify-end">
          <JurisdictionToggle v-if="showRegionsToggle" />
          <ThemeToggle />
          <LanguageToggle
            v-if="i18n_enabled"
            :compact="true"
            max-height="max-h-dvh" />
        </div>
      </div>
    </div>
  </footer>
</template>
